import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Paper,
  Chip,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  X,
  Download,
  Mail,
  UserPlus,
  Maximize2,
  Minimize2,
  MapPin,
  Clock,
  Star,
  Building2,
  Phone,
  FileText,
} from 'lucide-react';
import { toast } from '../../../ui/toast';
import { educationSummary } from '../utils/bankSearchAdapters';
import { apiUrl } from '../../../utils/api_service/api_url';
import { PDFDisplay } from '../../job_openings/create/ApplicantAnalysis/PDFDisplay';
import { CandidateDocument, CandidateType } from '../constants/mockData';
import { StatusHistoryTimeline } from '../../../ui/components/Timeline';

interface CandidateProfileModalProps {
  candidate: CandidateType | null;
  isOpen: boolean;
  onClose: () => void;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  getMatchColor: (percentage: number) => string;
  onInvite?: (candidate: CandidateType) => void;
}

export const CandidateProfileModal: React.FC<CandidateProfileModalProps> = ({
  candidate,
  isOpen,
  onClose,
  getStatusText,
  getMatchColor,
  onInvite,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'cv'>('profile');
  const [selectedDocument, setSelectedDocument] = useState<CandidateDocument | null>(null);
  const [docPreviewUrl, setDocPreviewUrl] = useState<string | null>(null);
  const [isDocLoading, setIsDocLoading] = useState<boolean>(false);

  // Synthesize list of documents from candidate.documents or candidate.filename
  const effectiveDocuments: CandidateDocument[] = React.useMemo(() => {
    if (!candidate) return [];
    const raw = candidate as any;
    if (candidate.documents && candidate.documents.length > 0) {
      return candidate.documents;
    }
    if (raw.applicant_documents && raw.applicant_documents.length > 0) {
      return raw.applicant_documents;
    }
    if (raw.applicantDocuments && raw.applicantDocuments.length > 0) {
      return raw.applicantDocuments;
    }

    const filename =
      candidate.filename ||
      raw.fileName ||
      raw.s3Filename ||
      raw.s3_filename ||
      candidate.pdfUrl ||
      raw.pdf_url ||
      raw.file_path ||
      raw.filePath ||
      raw.cv_filename ||
      raw.url;

    if (filename && typeof filename === 'string') {
      const baseName = filename.split('/').pop() || 'Resume.pdf';
      return [
        {
          id: 'main-resume',
          s3Filename: filename,
          baseFilename: baseName,
          requirementKey: 'resume',
          requirementLabel: 'Main CV / Resume',
        },
      ];
    }
    return [];
  }, [candidate]);

  useEffect(() => {
    if (effectiveDocuments.length > 0) {
      const resume = effectiveDocuments.find((d) => d.requirementKey === 'resume') || effectiveDocuments[0];
      setSelectedDocument(resume);
    } else {
      setSelectedDocument(null);
    }
  }, [effectiveDocuments]);

  useEffect(() => {
    let isCancelled = false;
    let createdBlobUrl: string | null = null;

    const resolveUrl = async () => {
      if (!candidate) {
        setDocPreviewUrl(null);
        setIsDocLoading(false);
        return;
      }

      setIsDocLoading(true);
      const raw = candidate as any;
      let targetFilename =
        selectedDocument?.s3Filename ||
        candidate?.filename ||
        raw?.fileName ||
        raw?.s3Filename ||
        raw?.s3_filename ||
        candidate?.pdfUrl ||
        raw?.pdf_url ||
        raw?.file_path ||
        raw?.filePath ||
        raw?.cv_filename ||
        raw?.url;

      if (!targetFilename && candidate.id) {
        try {
          const authToken = localStorage.getItem('token') || localStorage.getItem('adminToken') || '';
          const graphResp = await fetch(`${apiUrl}/job-applicant/2d-graph`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              additional_skills: { skill_weightage: false, similar: false, skills: [] },
              job_title: { similar: false, job_titles: [] },
              keywords: { keyword_weightage: false, similar: false, keywords: [] },
              behavioral_attributes: { similar: false, behavioral_attributes: [] },
              gender: [],
              age: { min_age: 0, max_age: 100, include_unknown: true },
              applicant_status: [],
              candidate_ids: [candidate.id],
            }),
          });

          if (graphResp.ok) {
            const graphData = await graphResp.json();
            const found = graphData.data?.find((d: any) => d.id === candidate.id || d.userId === candidate.id) || graphData.data?.[0];
            if (found) {
              targetFilename =
                found.filename ||
                found.fileName ||
                found.s3Filename ||
                found.s3_filename ||
                found.pdfUrl ||
                found.pdf_url ||
                found.file_path ||
                found.filePath ||
                found.cv_filename;
            }
          }
        } catch (e) {
          console.error('Error fetching candidate detail from 2d-graph:', e);
        }
      }

      if (!targetFilename || typeof targetFilename !== 'string') {
        setDocPreviewUrl(null);
        setIsDocLoading(false);
        return;
      }

      if (targetFilename.startsWith('http://') || targetFilename.startsWith('https://') || targetFilename.startsWith('blob:')) {
        setDocPreviewUrl(targetFilename);
        setIsDocLoading(false);
        return;
      }

      setIsDocLoading(true);
      try {
        const url = `${apiUrl}/storage?file_path=${encodeURIComponent(targetFilename)}`;
        const authToken = localStorage.getItem('token') || localStorage.getItem('adminToken') || '';

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: 'application/json',
          },
        });

        if (response.ok && !isCancelled) {
          const contentType = response.headers.get('content-type');
          let docUrl = '';

          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            docUrl = typeof data === 'string' ? data : data?.url || data?.signedUrl || data?.downloadUrl || data?.fileUrl || url;
          } else {
            docUrl = response.url || url;
          }

          if (docUrl && !isCancelled) {
            const filenameLower = targetFilename.toLowerCase();
            if (filenameLower.endsWith('.pdf')) {
              try {
                const s3Response = await fetch(docUrl);
                if (s3Response.ok && !isCancelled) {
                  const blob = await s3Response.blob();
                  const previewBlob = new Blob([blob], { type: 'application/pdf' });
                  createdBlobUrl = URL.createObjectURL(previewBlob);
                  setDocPreviewUrl(createdBlobUrl);
                } else if (!isCancelled) {
                  setDocPreviewUrl(docUrl);
                }
              } catch {
                if (!isCancelled) setDocPreviewUrl(docUrl);
              }
            } else {
              setDocPreviewUrl(docUrl);
            }
          }
        } else if (!isCancelled) {
          setDocPreviewUrl(`${apiUrl}/storage?file_path=${encodeURIComponent(targetFilename)}`);
        }
      } catch (err) {
        console.error('Error fetching document storage link:', err);
        if (!isCancelled) {
          setDocPreviewUrl(`${apiUrl}/storage?file_path=${encodeURIComponent(targetFilename)}`);
        }
      } finally {
        if (!isCancelled) {
          setIsDocLoading(false);
        }
      }
    };

    resolveUrl();

    return () => {
      isCancelled = true;
      if (createdBlobUrl) {
        URL.revokeObjectURL(createdBlobUrl);
      }
    };
  }, [selectedDocument, candidate]);

  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setActiveTab('profile');
    }
  }, [isOpen]);

  if (!candidate || !isOpen) return null;

  const scorePercent = Math.round(candidate.score * 100);
  const statusLabel = getStatusText(candidate.status) || candidate.status;

  const formatDate = (date?: string) => {
    if (!date) return 'Present';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getEducationSummary = () => educationSummary(candidate.educations);

  const handleDownloadCV = async () => {
    const raw = candidate as any;
    const filePath =
      selectedDocument?.s3Filename ||
      candidate?.filename ||
      raw?.fileName ||
      raw?.s3Filename ||
      raw?.s3_filename ||
      candidate?.pdfUrl ||
      raw?.pdf_url ||
      raw?.file_path ||
      raw?.filePath ||
      raw?.cv_filename ||
      raw?.url;

    if (!filePath) {
      toast.warn('No CV document available for download.');
      return;
    }

    const toastId = toast.info('Preparing download...', { autoClose: false });

    try {
      let downloadUrl = '';
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        downloadUrl = filePath;
      } else {
        const authToken = localStorage.getItem('token') || localStorage.getItem('adminToken') || '';
        const storageUrl = `${apiUrl}/storage?file_path=${encodeURIComponent(filePath)}`;

        const response = await fetch(storageUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Storage API error: ${response.status}`);
        }

        const data = await response.json();
        downloadUrl = typeof data === 'string' ? data : data?.url || data?.signedUrl || data?.downloadUrl || data?.fileUrl;
      }

      if (!downloadUrl) {
        throw new Error('No downloadable URL found in storage API response');
      }

      const cleanPath = (filePath || downloadUrl).split('?')[0];
      const ext = cleanPath.split('.').pop()?.toLowerCase();
      const validExtension = ext && ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg'].includes(ext) ? ext : 'pdf';
      const candidateName = (candidate?.name || 'Candidate')
        .trim()
        .replace(/[^a-zA-Z0-9_\-\s]/g, '')
        .replace(/\s+/g, '_');
      const downloadFilename = `${candidateName}_CV.${validExtension}`;

      let downloaded = false;
      try {
        const fileResponse = await fetch(downloadUrl);
        if (fileResponse.ok) {
          const blob = await fileResponse.blob();
          if (blob && blob.size > 0) {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(blobUrl);
            }, 3000);
            downloaded = true;
          }
        }
      } catch {
        // Fallback below
      }

      if (!downloaded) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = downloadFilename;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 1000);
      }

      toast.dismiss(toastId);
      toast.success('Download started!');
    } catch (error) {
      console.error('CV download error:', error);
      toast.dismiss(toastId);
      toast.error('Download failed. Please try again.');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={isExpanded ? false : 'lg'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          height: isExpanded ? '95vh' : '90vh',
          maxWidth: isExpanded ? '98vw' : 1300,
          display: 'flex',
          flexDirection: 'column',
          m: 1,
        },
      }}
    >
      {/* Header Bar */}
      <DialogTitle
        sx={{
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {/* Left: Avatar + Profile details + Match Score */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <Avatar
            src={candidate.avatar}
            sx={{ width: 44, height: 44, bgcolor: 'grey.900', color: 'common.white', fontWeight: 700 }}
          >
            {candidate.name.split(' ').map(p => p[0]).join('').slice(0, 2)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, color: 'text.disabled', fontSize: '0.625rem' }}>
              Candidate Profile
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>
                {candidate.name}
              </Typography>
              <Box
                className={getMatchColor(scorePercent)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1,
                  py: 0.25,
                  borderRadius: 4,
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  color: 'common.white',
                }}
              >
                {scorePercent}% Match
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
              <Chip
                icon={<MapPin size={12} />}
                label={candidate.location ?? 'Location unknown'}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.6875rem' }}
              />
              <Chip
                icon={<Clock size={12} />}
                label={`${Math.floor(candidate.experience)} yrs exp`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.6875rem' }}
              />
              <Chip
                label={statusLabel}
                size="small"
                color="success"
                sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 600 }}
              />
            </Stack>
          </Box>
        </Stack>

        {/* Right: Actions */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: { xs: 0, sm: 'auto' }, flexShrink: 0, flexWrap: 'wrap' }}>
          {/* Mobile Tab Toggle */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, bgcolor: 'grey.200', p: 0.25, borderRadius: 2 }}>
            <Button
              size="small"
              onClick={() => setActiveTab('profile')}
              variant={activeTab === 'profile' ? 'contained' : 'text'}
              sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none' }}
            >
              Profile
            </Button>
            <Button
              size="small"
              onClick={() => setActiveTab('cv')}
              variant={activeTab === 'cv' ? 'contained' : 'text'}
              color="success"
              sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none' }}
            >
              CV Viewer
            </Button>
          </Box>

          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
          >
            {isExpanded ? 'Collapse' : 'Full View'}
          </Button>

          {docPreviewUrl && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<Download size={14} />}
              onClick={handleDownloadCV}
              sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
            >
              Download CV
            </Button>
          )}

          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<Mail size={14} />}
            onClick={() => {
              if (candidate.email) window.location.href = `mailto:${candidate.email}`;
            }}
            sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
          >
            Email
          </Button>

          <Button
            size="small"
            variant="contained"
            startIcon={<UserPlus size={14} />}
            onClick={() => onInvite?.(candidate)}
            sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
          >
            Invite
          </Button>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{ border: '1px solid', borderColor: 'grey.300', color: 'text.secondary' }}
          >
            <X size={16} />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Content Split Container */}
      <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, overflow: 'hidden' }}>
        {/* Left Side: Candidate Profile Details */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: { xs: 2, sm: 2.5 },
            borderRight: { xs: 'none', lg: '1px solid' },
            borderColor: 'grey.200',
            display: activeTab === 'cv' ? { xs: 'none', lg: 'flex' } : 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Multi-Document Selector Tabs */}
          {effectiveDocuments.length > 1 && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileText size={14} color="#059669" />
                Attached Documents ({effectiveDocuments.length})
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {effectiveDocuments.map((doc) => {
                  const isSelected = selectedDocument?.s3Filename === doc.s3Filename;
                  return (
                    <Button
                      key={doc.id || doc.s3Filename}
                      size="small"
                      variant={isSelected ? 'contained' : 'outlined'}
                      color={isSelected ? 'success' : 'inherit'}
                      onClick={() => {
                        setSelectedDocument(doc);
                        setActiveTab('cv');
                      }}
                      sx={{ textTransform: 'none', fontSize: '0.6875rem', fontWeight: 700 }}
                    >
                      {doc.requirementLabel || doc.baseFilename || 'Document'}
                    </Button>
                  );
                })}
              </Stack>
            </Paper>
          )}

          {/* Professional Snapshot */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.100',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, color: 'text.disabled', fontSize: '0.625rem' }}>
                  Candidate Summary
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Professional Snapshot
                </Typography>
              </Box>
              <Chip
                icon={<Star size={12} color="#f59e0b" />}
                label="Top Talent"
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600 }}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1, lineHeight: 1.6 }}>
              {getEducationSummary() || 'Strong candidate with proven experience and technical skills across multiple domain projects.'}
            </Typography>
          </Paper>

          {/* Skills & Expertise */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Skills & Expertise
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Core technical proficiencies.
                </Typography>
              </Box>
              <Chip
                label={`${candidate.skills.length} Skills`}
                size="small"
                sx={{ height: 20, fontSize: '0.625rem', fontWeight: 700 }}
              />
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {candidate.skills.map((skill) => (
                <Chip
                  key={skill}
                  label={skill}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', fontWeight: 600, bgcolor: 'grey.50' }}
                />
              ))}
            </Stack>
          </Paper>

          {/* Professional Experience */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Professional Experience
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Work history and roles.
                </Typography>
              </Box>
              <Chip
                icon={<Building2 size={12} />}
                label={`${candidate.work_experience?.length ?? 0} roles`}
                size="small"
                sx={{ height: 20, fontSize: '0.625rem', fontWeight: 600 }}
              />
            </Stack>
            <Stack spacing={1.5}>
              {(candidate.work_experience ?? []).map((exp, index) => (
                <Paper
                  key={`${exp.job_title}-${index}`}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.100',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {exp.job_title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                      {exp.start_date ? formatDate(exp.start_date) : ''}
                      {exp.start_date || exp.end_date ? ' — ' : ''}
                      {formatDate(exp.end_date)}
                    </Typography>
                  </Stack>
                  {exp.company && (
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, display: 'block', mt: 0.25 }}>
                      {exp.company}
                    </Typography>
                  )}
                  {exp.description && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, lineHeight: 1.5 }}>
                      {exp.description}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>
          </Paper>

          {/* Status History Timeline */}
          <StatusHistoryTimeline
            statusHistory={(candidate as any)?.statusHistory}
            currentStatus={candidate.status || (candidate as any)?.applicantStatus}
            createdAt={(candidate as any)?.createdAt}
            title="Status Timeline"
            collapsible={true}
          />

          {/* Contact & Candidate Stats Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {/* Contact Information */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                Contact Information
              </Typography>
              <Stack spacing={1}>
                <Paper elevation={0} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Mail size={16} color="#64748b" />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.disabled', display: 'block' }}>
                      Email
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {candidate.email}
                    </Typography>
                  </Box>
                </Paper>
                <Paper elevation={0} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone size={16} color="#64748b" />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.disabled', display: 'block' }}>
                      Phone
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block' }}>
                      {candidate.phone ?? 'No phone provided'}
                    </Typography>
                  </Box>
                </Paper>
              </Stack>
            </Paper>

            {/* Stats */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                bgcolor: 'grey.50',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}>
                Candidate Stats
              </Typography>
              <Stack spacing={1}>
                <Paper elevation={0} sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Match Score</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>{scorePercent}%</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total Experience</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>{Math.floor(candidate.experience)} Years</Typography>
                </Paper>
              </Stack>
            </Paper>
          </Box>
        </Box>

        {/* Right Side: Uploaded CV / PDF Document Viewer Pane */}
        <Box
          sx={{
            flex: 1,
            bgcolor: 'grey.900',
            minWidth: 0,
            display: activeTab === 'profile' ? { xs: 'none', lg: 'flex' } : 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'grey.800',
              borderBottom: '1px solid',
              borderColor: 'grey.700',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(16, 185, 129, 0.2)',
                  color: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FileText size={16} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'common.white', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedDocument?.requirementLabel || 'CV / Resume Document'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.6875rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedDocument?.baseFilename || candidate.filename?.split('/').pop() || `${candidate.name}_CV.pdf`}
                </Typography>
              </Box>
            </Stack>

            {docPreviewUrl && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<Download size={14} />}
                onClick={handleDownloadCV}
                sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
              >
                Download
              </Button>
            )}
          </Box>

          {/* Document Render Window */}
          <Box sx={{ flex: 1, position: 'relative', bgcolor: 'grey.950', display: 'flex', flexDirection: 'column' }}>
            {isDocLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, p: 4 }}>
                <CircularProgress size={36} color="success" />
                <Typography variant="caption" sx={{ color: 'grey.400', mt: 2 }}>
                  Loading candidate document...
                </Typography>
              </Stack>
            ) : docPreviewUrl ? (
              <PDFDisplay pdfUrl={docPreviewUrl} />
            ) : (
              <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1, p: 4, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    bgcolor: 'grey.800',
                    color: 'grey.400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FileText size={28} />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'grey.300' }}>
                    No CV File Preview Available
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'grey.500', maxWidth: 300, display: 'block', mt: 0.5 }}>
                    This candidate record does not have a linked PDF or document file to display.
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
